import { CheckoutProps } from '@containers/types';
import GuestLayout from '@layouts/guest';
import { Grid } from '@material-ui/core';
import { observer } from 'mobx-react';
import useStore from '@hooks/useStore';
import { FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import useStyles from '@containers/Sura/styles';
import Stepper from '@components/Sura/Stepper';
import PaymentDetails from '@components/Sura/PaymentDetails';
import FooterSura from '@components/Sura/Footer';
import { CheckoutSuraParams } from '@containers/types';
import { PaymentLinkStatus } from '@constants/paymentLink';
import routes from '@constants/routes';
import { appUrl } from '@configs/variables';

export const routesSura = [
  routes.SURA_CHECKOUT,
  routes.SURA_CHECKOUT_ONLINEPAYMENT,
  routes.SURA_CHECKOUT_ONLINEPAYMENT_CARDVISA,
  routes.SURA_CHECKOUT_ONLINEPAYMENT_CARDAMEX,
  routes.SURA_CHECKOUT_ONLINEPAYMENT_CLABE,
  routes.SURA_CHECKOUT_CASH,
  routes.SURA_CHECKOUT_BANK_REFERENCE,
  routes.SURA_CHECKOUT_BANK_REFERENCE_SENDREFERENCE,
  routes.SURA_CHECKOUT_BANK_REFERENCE_DOWNLOADREFERENCE,
  routes.SURA_CHECKOUT_PAYMENT_SUCCESS,
  routes.SURA_CHECKOUT_SEND_REFERENCE,
];

const Sura: FC<CheckoutProps> = () => {
  const { links, sura, checkout, ui } = useStore();
  const params = useParams<CheckoutSuraParams>();

  const linkId = params.paymentLinkId;
  const css = useStyles({ isMobile: ui.isMobileScreen });
  const isLoading = links.flags.isRequesting || !links.paymentLink.exists;
  const isPaymentSuccess =
    checkout.success.cardPayment ||
    links.paymentLink.isPaid ||
    (links.paymentLink.isSubscribed && !links.paymentLink.sura.onlySubscription) ||
    links.paymentLink.status === PaymentLinkStatus.PENDING_ACTIVATION ||
    checkout.success.directDebitPayment ||
    links.paymentLink.status === PaymentLinkStatus.SUBSCRIPTION_CANCELED;
  const isExpired = links.paymentLink.status === PaymentLinkStatus.EXPIRED;
  const isOnlySubscription = links.paymentLink.sura.onlySubscription;
  const isSubscriptionSuccess = checkout.success.subscription;

  useEffect(() => {
    if (isExpired) {
      sura.setStep(3);
      sura.setPaymentExpired(true);
    }
  }, [isExpired]);

  useEffect(() => {
    if (
      (links.paymentLink.isSubscribed && links.paymentLink.sura.onlySubscription) ||
      isSubscriptionSuccess
    ) {
      if (links.paymentLink.settings?.redirectAuto === 'true') {
        window.location.replace(links.paymentLink.settings?.redirectUrl);
        return;
      }
      sura.setStep(3);
    }
  }, [
    links.paymentLink.isSubscribed,
    links.paymentLink.sura.onlySubscription,
    isSubscriptionSuccess,
  ]);

  useEffect(() => {
    if (isPaymentSuccess) {
      sura.setStep(3);
      history.pushState(
        null,
        '',
        `${appUrl}/sura/pay/${links.paymentLink.paymentLinkId}/payment-success`,
      );
    } else {
      sura.setStep(0);
    }
  }, [isPaymentSuccess]);

  useEffect(() => {
    links.getPaymentLink(linkId);
  }, [linkId]);

  useEffect(() => {
    if (checkout.success.cardPayment) {
      links.setAsPaid();
    }
  }, [checkout.success.cardPayment]);

  useEffect(() => {
    sura.setBaseUrl(`${location.href.split('/payment-methods')[0]}/payment-methods`);
    sura.setIsSura(true);
  }, []);

  const calculateXs = () => {
    if (ui.isMobileScreen) return 12;
    if (sura.step === 2 && isOnlySubscription) return 12;
    if (sura.step < 3) return 8;
    return 12;
  };

  return (
    <GuestLayout loading={isLoading}>
      <Grid xs={12} container>
        <Grid xs={12} container className={css.root}>
          <Grid
            item
            xs={calculateXs()}
            className={
              isOnlySubscription && sura.step > 1
                ? css.subscriptionPaymentMethod
                : css.paymentMethod
            }
          >
            <Stepper isPaymentSuccess={isPaymentSuccess} />
          </Grid>
          <Grid item xs={ui.isMobileScreen ? 12 : 4} className={css.paymentMethod}>
            {sura.step < 3 && !ui.isMobileScreen && !isOnlySubscription && <PaymentDetails />}
          </Grid>
        </Grid>
        <FooterSura />
      </Grid>
    </GuestLayout>
  );
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default observer(Sura);
